import { Button, Card, CardContent, Input } from '@/components/ui';
import { useContracts } from '@/hooks/use-contracts';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { useTranslation } from '@kgc/i18n';
import { DataTable } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONTRACT_STATUSES, CONTRACT_TYPES } from './mock-data';
import type { Contract, ContractStatus, ContractType } from './types';

export function ContractListPage() {
  const { t, locale } = useTranslation();
  const LOCALE_MAP: Record<string, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE', uk: 'uk-UA' };
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<ContractType | 'all'>('all');

  // Server-mode pagination (BE-S2 = A): a lista eddig page/pageSize nélkül hívott
  // → CSAK a BE-default első ~20 szerződést mutatta (a 21+ némán láthatatlan volt).
  const serverPagination = useServerPagination(20);

  const { contracts, total, isLoading, error } = useContracts({
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    ...serverPagination.query,
  });

  // Szűrő-váltáskor ugorj az 1. oldalra.
  useEffect(() => {
    serverPagination.reset();
  }, [searchTerm, statusFilter, typeFilter, serverPagination.reset]);

  const filteredContracts = useMemo(() => {
    // Server-side filtering is used; client-side fallback for immediate UI response
    return contracts;
  }, [contracts]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(LOCALE_MAP[locale] ?? 'hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(LOCALE_MAP[locale] ?? 'hu-HU');
  };

  const getStatusBadge = (status: ContractStatus) => {
    const config = CONTRACT_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const getTypeBadge = (type: ContractType) => {
    const config = CONTRACT_TYPES.find(tp => tp.value === type);
    return (
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : type}
      </span>
    );
  };

  // Column definitions a megosztott DataTable-höz (a cella-renderek megőrzik az eredeti markup-ot).
  const columns: ColumnDef<Contract>[] = [
    {
      id: 'contract',
      header: t('contract.contract'),
      cell: ({ row }) => {
        const contract = row.original;
        return (
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {contract.contractNumber}
            </p>
            <div className="mt-1 flex items-center gap-2">
              {getTypeBadge(contract.type)}
              {contract.rentalNumber && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {contract.rentalNumber}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'partner',
      header: t('table.partner'),
      cell: ({ row }) => {
        const contract = row.original;
        return (
          <>
            <p className="text-gray-900 dark:text-gray-100">{contract.partnerName}</p>
            {contract.partnerPhone && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{contract.partnerPhone}</p>
            )}
          </>
        );
      },
    },
    {
      id: 'period',
      header: t('table.period'),
      cell: ({ row }) => {
        const contract = row.original;
        return (
          <>
            <p className="text-gray-900 dark:text-gray-100">{formatDate(contract.startDate)}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              → {formatDate(contract.actualEndDate ?? contract.expectedEndDate)}
            </p>
          </>
        );
      },
    },
    {
      id: 'amount',
      header: () => <div className="text-right">{t('common.amount')}</div>,
      cell: ({ row }) => (
        <p className="text-right font-medium text-gray-900 dark:text-gray-100">
          {formatPrice(row.original.totalAmount)}
        </p>
      ),
    },
    {
      id: 'deposit',
      header: () => <div className="text-right">{t('contract.deposit')}</div>,
      cell: ({ row }) => {
        const contract = row.original;
        return (
          <div className="text-right">
            <p
              className={`font-medium ${contract.depositPaid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
            >
              {formatPrice(contract.depositAmount)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {contract.depositPaid
                ? `✓ ${t('contract.depositPaid')}`
                : `✗ ${t('contract.depositMissing')}`}
            </p>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: () => <div className="text-center">{t('table.status')}</div>,
      cell: ({ row }) => <div className="text-center">{getStatusBadge(row.original.status)}</div>,
    },
  ];

  // Stats (computed from loaded data)
  const stats = useMemo(
    () => ({
      total: contracts.length,
      active: contracts.filter(c => c.status === 'active').length,
      draft: contracts.filter(c => c.status === 'draft').length,
      totalDeposit: contracts
        .filter(c => c.status === 'active')
        .reduce((sum, c) => sum + c.depositAmount, 0),
      totalValue: contracts
        .filter(c => c.status === 'active')
        .reduce((sum, c) => sum + c.totalAmount, 0),
    }),
    [contracts]
  );

  if (error) {
    return (
      <div className="kgc-bg flex min-h-screen items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg">
      {isLoading && (
        <div className="py-4 text-center text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('common.loading')}
        </div>
      )}
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('contract.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('contract.subtitle')}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/rental')} className="bg-primary hover:bg-primary/90">
            {t('contract.goToRentals')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.all')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.active}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('status.active')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{stats.draft}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('status.draft')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {formatPrice(stats.totalDeposit)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('contract.activeDeposit')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatPrice(stats.totalValue)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('contract.activeValue')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[250px] flex-1">
                <Input
                  placeholder={t('contract.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value as ContractType | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('page.allTypes')}</option>
                {CONTRACT_TYPES.map(tp => (
                  <option key={tp.value} value={tp.value}>
                    {t(tp.labelKey)}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as ContractStatus | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('page.allStatuses')}</option>
                {CONTRACT_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {t(s.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Contracts list */}
        <Card>
          <CardContent className="p-0">
            <DataTable
              columns={columns}
              data={filteredContracts}
              emptyMessage={t('page.noFilterResults')}
              getRowId={contract => contract.id}
              onRowClick={contract => navigate(`/contracts/${contract.id}`)}
              {...(typeof total === 'number' ? serverPagination.tableProps(total) : {})}
              rowActions={contract => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/contracts/${contract.id}`)}
                >
                  {t('common.details')}
                </Button>
              )}
            />
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('contract.contractsDisplayed', { count: filteredContracts.length })}
        </p>

        {/* Info box */}
        <Card className="mt-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  {t('contract.autoGenerationTitle')}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-300">
                  {t('contract.autoGenerationDesc')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
